/**
 * What a "delete" actually does, and therefore what the dialog must say.
 *
 * Three different acts wear the word "delete" in this app, and they have very
 * different consequences. A confirmation dialog that overstates is worse than none
 * at all: people learn that the scary wording is not true and click through it, and
 * then it fails to protect them on the one occasion it mattered.
 *
 * So the copy is derived here, from the bid, and tested — rather than written once
 * into JSX and hoped over.
 */

export type RemovalScope = 'server-permanent' | 'server-hide' | 'device-only';

export interface RemovalCopy {
  scope: RemovalScope;
  /** Dialog heading. Ends in a question mark: it is asking. */
  title: string;
  body: string;
  /** Text on the destructive button. Shouty, per house style for irreversible acts. */
  confirmLabel: string;
  /** True only when the act genuinely cannot be undone. */
  irreversible: boolean;
}

/**
 * Removing a bid from the server.
 *
 * `reachedCustomer` mirrors the server's own test: a bid that was sent, accepted or
 * declined — or that any external system holds a reference to — is hidden rather
 * than destroyed, because somebody was quoted that number.
 */
export function describeServerRemoval(bid: {
  status: string;
  customerName: string | null;
  totalCents: number;
}): RemovalCopy {
  const who = bid.customerName?.trim() || 'this unnamed customer';
  const reachedCustomer = ['sent', 'accepted', 'declined'].includes(bid.status);

  if (reachedCustomer) {
    return {
      scope: 'server-hide',
      title: 'Hide this bid?',
      body:
        `This bid was ${bid.status}, so it will be hidden rather than destroyed — ${who} has ` +
        'already seen this price. You can restore it later from "Show deleted".',
      confirmLabel: 'HIDE',
      irreversible: false,
    };
  }

  return {
    scope: 'server-permanent',
    title: 'Permanently delete this bid?',
    body:
      `The bid for ${who} will be deleted for good, along with its measurements and ` +
      'breakdown. This cannot be undone. The customer record is kept.',
    confirmLabel: 'DELETE',
    irreversible: true,
  };
}

/**
 * Removing a bid from the device you are holding.
 *
 * Deliberately NOT described as permanent. A synced bid still exists on the server,
 * and claiming otherwise would train people to distrust the dialog.
 */
export function describeDeviceRemoval(bid: {
  sync: 'pending' | 'synced' | 'blocked';
  customerName: string | null;
}): RemovalCopy {
  const who = bid.customerName?.trim() || 'this unnamed customer';

  if (bid.sync === 'blocked') {
    return {
      scope: 'device-only',
      title: 'Remove from this device?',
      body:
        `The bid for ${who} could not be sent to the office — usually because an administrator ` +
        'deleted it. Removing it here just clears it from this phone.',
      confirmLabel: 'REMOVE',
      irreversible: false,
    };
  }

  return {
    scope: 'device-only',
    title: 'Remove from this device?',
    body:
      `The bid for ${who} has already synced, so the office keeps its copy. This only clears ` +
      'it from this phone to tidy your list.',
    confirmLabel: 'REMOVE',
    irreversible: false,
  };
}
